import { authAPI } from './api';

class TokenService {
  constructor() {
    this.accessTokenKey = 'accessToken';
    this.userKey = 'user';
  }

  // The refresh token lives in an httpOnly cookie set by the backend; only the
  // short-lived access token is kept in localStorage.
  setTokens(accessToken) {
    if (accessToken) {
      localStorage.setItem(this.accessTokenKey, accessToken);
    }
  }

  // Get access token from localStorage
  getAccessToken() {
    return localStorage.getItem(this.accessTokenKey);
  }

  // Store user data
  setUser(user) {
    if (user) {
      localStorage.setItem(this.userKey, JSON.stringify(user));
    }
  }

  // Get user data
  getUser() {
    const userData = localStorage.getItem(this.userKey);
    return userData ? JSON.parse(userData) : null;
  }

  // Clear all stored data ('refreshToken' is a leftover from pre-cookie builds)
  clearTokens() {
    localStorage.removeItem(this.accessTokenKey);
    localStorage.removeItem('refreshToken');
    localStorage.removeItem(this.userKey);
  }

  // Check if user is authenticated
  isAuthenticated() {
    return !!this.getAccessToken();
  }

  // Refresh access token using the httpOnly refresh cookie
  async refreshAccessToken() {
    try {
      const response = await authAPI.refreshToken();

      if (response.success) {
        const { accessToken, user } = response.data;

        this.setTokens(accessToken);
        this.setUser(user);

        return accessToken;
      } else {
        throw new Error(response.message || 'Token refresh failed');
      }
    } catch (error) {
      console.error('Token refresh failed:', error);
      this.clearTokens();
      throw error;
    }
  }

  // Logout current session (backend revokes the cookie's token and clears it)
  async logout() {
    try {
      await authAPI.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      this.clearTokens();
    }
  }

  // Logout from all devices
  async logoutAll() {
    try {
      await authAPI.logoutAll();
    } catch (error) {
      console.error('Logout all error:', error);
    } finally {
      this.clearTokens();
    }
  }

  // Get active sessions
  async getActiveSessions() {
    try {
      const response = await authAPI.getActiveSessions();
      if (response.success) {
        return response.data.sessions;
      } else {
        throw new Error(response.message || 'Failed to get sessions');
      }
    } catch (error) {
      console.error('Get sessions error:', error);
      throw error;
    }
  }

  // Revoke a specific session
  async revokeSession(sessionId) {
    try {
      const response = await authAPI.revokeSession(sessionId);
      if (response.success) {
        return true;
      } else {
        throw new Error(response.message || 'Failed to revoke session');
      }
    } catch (error) {
      console.error('Revoke session error:', error);
      throw error;
    }
  }
}

const tokenService = new TokenService();
export default tokenService;
